/**
 * Aus welchen Punkten sich Daten holen lassen (Weg 2, PLAN-punktdaten-uebernehmen.md).
 *
 * Nutzer-Entscheidung 28.09.2026: **nur Punkte des aktiven Projekts**, die nächsten
 * zuerst, mit Suche nach Nummer. Die Projekt-Zugehörigkeit entscheidet der Aufrufer
 * (`ProjectService.isPointAssignedToActiveProject`, dieselbe Stelle wie überall) - hier
 * wird sie nicht ein zweites Mal ausgeschrieben.
 *
 * Grundlage ist die Punktliste, die die Reiter-Ansicht ohnehin lädt (Index-Sicht mit
 * dem ganzen Kopf). Das Verfahren (SEA/KA) steht dort nicht; geprüft wird es erst am
 * vollen Datensatz, sobald ein Punkt gewählt ist.
 */

import { istPunktartMitProtokoll } from '../../data-model';

export interface Quellpunkt {
	id: string;
	nummer: string;
	punktart: string;
	/** Aufnahmedatum als ISO-Text, wie es im Datensatz steht. */
	datum?: string;
	/** Entfernung zum Zielpunkt in Metern; fehlt ohne gültige Koordinaten. */
	abstandM?: number;
}

/** Was aus einem Eintrag der Punktliste gebraucht wird. */
export interface PunktImIndex {
	_id: string;
	type: string;
	header?: { nummer?: string; aufnahme?: { datum?: string } };
}

/**
 * Die Punkte, aus denen der Zielpunkt Daten holen kann: im aktiven Projekt, mit
 * Protokoll, nicht er selbst. Sortiert nach Entfernung, Punkte ohne Entfernung ans Ende,
 * bei gleicher Entfernung nach Nummer.
 */
export function quellpunkteFuer<P extends PunktImIndex>(
	punkte: readonly P[],
	ziel: { _id?: string },
	gehoertZumProjekt: (p: P) => boolean,
	abstandM: (p: P) => number | undefined,
): Quellpunkt[] {
	return punkte
		.filter(p => p && p._id !== ziel._id && istPunktartMitProtokoll(p.type) && gehoertZumProjekt(p))
		.map(p => {
			const abstand = abstandM(p);
			return {
				id: p._id,
				nummer: String(p.header?.nummer ?? '').trim(),
				punktart: p.type,
				datum: p.header?.aufnahme?.datum,
				abstandM: typeof abstand === 'number' && Number.isFinite(abstand) ? abstand : undefined,
			};
		})
		.sort((a, b) => {
			const aa = a.abstandM ?? Number.POSITIVE_INFINITY;
			const bb = b.abstandM ?? Number.POSITIVE_INFINITY;
			if (aa !== bb) return aa - bb;
			return a.nummer.localeCompare(b.nummer, 'de', { numeric: true });
		});
}

/** Suche nach Nummer: Teiltreffer, Groß-/Kleinschreibung egal. Leere Suche = alle. */
export function sucheQuellpunkte(liste: readonly Quellpunkt[], text: string | null | undefined): Quellpunkt[] {
	const s = (text ?? '').trim().toLowerCase();
	if (!s) return [...liste];
	return liste.filter(q => q.nummer.toLowerCase().includes(s));
}

/** Entfernung zum Lesen: unter 1 km in Metern, darüber in Kilometern mit einer Stelle. */
export function abstandText(abstandM: number | undefined): string {
	if (abstandM === undefined) return '';
	if (abstandM < 1000) return `${Math.round(abstandM)} m`;
	return `${(abstandM / 1000).toFixed(1).replace('.', ',')} km`;
}
